import { useEffect, useState } from "react";
import "./CrudContentPopup.scss";
import ModalContainer from "../ModalContainer";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useNotification from "../../../hooks/notification.hook";
import usePlanCopyConf from "../../../hooks/plan-copy-conf.hook";
import {
	createCopier,
	createCopierContent,
	createCopierRequirement,
	deleteCopierContent,
	deleteCopierRequirement,
	updateCopier,
} from "../../../services/organizations.service";
import BoxPlanOrganizationTemplate from "../BoxPlanOrganizationTemplate";
import { getCopierSelection } from "../../../helper/formatter.helper";

export default function CrudContentModal({ open, setOpen, confModel, template, organization }) {
	const { t } = useTranslation();

	const { setUpdateListConf, selectedOrganization } = usePlanCopyConf();

	// Set message
	yup.setLocale({
		// use constant translation keys for messages without values
		mixed: {
			required: ({ path }) => t("common:errors.required", { field: t(`common:fields.${path}`) }),
		},
		number: {
			moreThan: ({ path, more }) =>
				t("common:errors.more_than", {
					field: t(`common:fields.${path}`),
					value: more,
				}),
			lessThan: ({ path, less }) =>
				t("common:errors.less_than", {
					field: t(`common:fields.${path}`),
					value: less,
				}),
		},
	});

	// Schema Yup
	const schema = yup.object().shape({
		name: yup.string().required(),
	});

	const {
		register,
		setValue,
		trigger,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);

	const [selectedChapters, setSelectedChapters] = useState({
		chapters: [],
		contents: [],
		goals: [],
		reqs: [],
	});

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function confirmCopy(data) {
		if (selectedChapters.chapters.length > 0 && !loading) {
			setLoading(true);

			const organizationContentIdsByTemplateId = new Map(
				template
					.flatMap((chapter) => chapter.contents)
					.filter((content) => content.content_type === "chapter_content")
					.map((content) => [content.id, content.organization_chapter_content_id ?? content.id])
			);
			const organizationRequirementIdsByTemplateId = new Map(
				template
					.flatMap((chapter) => chapter.contents)
					.filter((content) => content.content_type === "chapter_goal")
					.flatMap((goal) => goal.requirements)
					.map((requirement) => [
						requirement.id,
						requirement.organization_requirement_id ?? requirement.id,
					])
			);

			const selectedOrganizationContentIds = selectedChapters.contents.map(
				(content) => organizationContentIdsByTemplateId.get(content.id) ?? content.id
			);
			const selectedOrganizationRequirementIds = selectedChapters.reqs.map(
				(requirement) =>
					organizationRequirementIdsByTemplateId.get(requirement.id) ?? requirement.id
			);

			let copier = null;

			if (!confModel?.id) {
				const newCopier = await createCopier({
					id: organization?.id ?? 0,
					idPlan: selectedOrganization?.plan?.id ?? 0,
					...data,
				});
				copier = newCopier.data;
			} else {
				if (data.name !== confModel.name)
					updateCopier({
						idCorp: organization?.id ?? 0,
						id: confModel.id,
						...data,
					});

				copier = confModel;
			}

			let contentsToCreate = selectedOrganizationContentIds;

			let requirementToCreate = selectedOrganizationRequirementIds;

			let contentsToDelete = [];

			let requirementToDelete = [];

			if (confModel) {
				const copierContens = confModel.copier_chapter_contents.map(
					(content) => content.organization_chapter_content_id
				);

				const copierRequirements = confModel.copier_requirements.map(
					(req) => req.organization_requirement_id
				);

				contentsToCreate = contentsToCreate.filter((el) => !copierContens.includes(el));

				requirementToCreate = requirementToCreate.filter((el) => !copierRequirements.includes(el));

				const findContensDelete = copierContens.filter(
					(el) => !selectedOrganizationContentIds.includes(el)
				);

				contentsToDelete = confModel.copier_chapter_contents
					.filter((el) => findContensDelete.includes(el.organization_chapter_content_id))
					.map((el) => el.id);

				const findReqsDelete = copierRequirements.filter(
					(el) => !selectedOrganizationRequirementIds.includes(el)
				);

				requirementToDelete = confModel.copier_requirements
					.filter((el) => findReqsDelete.includes(el.organization_requirement_id))
					.map((el) => el.id);
			}

			// Delete reqs
			for (const req of requirementToDelete) {
				await deleteCopierRequirement({
					idCorp: organization?.id ?? 0,
					id: req,
				});
			}

			// Delete contents
			for (const content of contentsToDelete) {
				await deleteCopierContent({
					idCorp: organization?.id ?? 0,
					id: content,
				});
			}

			// add content
			for (const content of contentsToCreate) {
				await createCopierContent({
					idCorp: organization?.id ?? 0,
					idCopier: copier.id,
					organization_chapter_content_id: content,
				});
			}

			// Add reqs
			for (const req of requirementToCreate) {
				await createCopierRequirement({
					idCorp: organization?.id ?? 0,
					idCopier: copier.id,
					organization_requirement_id: req,
				});
			}

			setUpdateListConf((i) => i + 1);

			setLoading(false);

			setOpen(false);
		} else {
			openNotification({
				text: "Nessun contenuto da copiare selezionato",
				status: "error",
			});
		}
	}

	useEffect(() => {
		if (open) {
			setValue("name", confModel ? confModel.name : "");

			if (!confModel) {
				setSelectedChapters({
					chapters: [],
					contents: [],
					goals: [],
					reqs: [],
				});
			} else {
				setSelectedChapters(getCopierSelection(template, confModel));
			}
		}
	}, [open]);

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen} disableClose={loading}>
			<div className="main_modal_content conf_plan_popup">
				<button
					className="main_close"
					onClick={() => !loading && setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				<div className="main_header_modal">
					<span className="title">
						{confModel?.id ? "Modifica configurazione" : "Nuova configurazione"}
					</span>

					<div className="main_action_detail">
						<form
							id="content-conf-form"
							onSubmit={handleSubmit(confirmCopy)}
							className="global_form_landing"
						>
							<div className="base">
								<div className="wrap_input_landing">
									<label className="label_input" htmlFor="name_conf">
										Nome
									</label>
									<input
										type="text"
										{...register("name")}
										className={`landing${errors.name ? " err" : ""}`}
										id="name_conf"
									/>
								</div>
							</div>
						</form>
					</div>
				</div>

				<div className={"main_body_modal"}>
					<OverlayScrollbarsComponent
						defer
						options={{ scrollbars: { autoHide: "scroll" } }}
						className="main_list_capthers"
					>
						{/* Chapters */}
						<BoxPlanOrganizationTemplate
							template={template}
							setSelectedItems={setSelectedChapters}
							showAllContents={false}
							selectedItems={selectedChapters}
						/>
					</OverlayScrollbarsComponent>
				</div>

				<div className="main_footer_modal">
					<div className="right_content">
						{/* Main Cta */}
						<button
							type="submit"
							form="content-conf-form"
							className={`button fill success${loading ? " loading" : ""}`}
						>
							<div className="load_content">Conferma</div>
							<div className="main_loader_icon fade_in"></div>
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
}
